/**
 * Sharing a listing, an open house or an article to a social network.
 *
 * Every platform here takes the *link* and builds its own preview card by
 * fetching it — none of them lets us pass a title, a photo and a price
 * directly. Facebook's `sharer.php` in particular ignores everything but
 * `u` and `quote`. So what a shared page looks like is decided by the
 * Open Graph tags the backend renders, not by anything in this file; see
 * SHARE-PREVIEWS.md and docs/SOCIAL-PUBLISHING.md.
 *
 * What this file does own: the canonical URL of each share target (and
 * its `?src=` fallback when a tracked short link cannot be minted), the
 * caption that gets prefilled, the composer URL of every network, and
 * opening the composer.
 */

import { APP_BASE_URL, API_BASE_URL } from '@/config/api';
import { PATHS, buildPath } from '@/routes/paths';
import { notify } from '@/utils/notify';

/**
 * Where a share can go. Instagram is here even though it accepts no
 * shared link — see `shareIntentUrl`. `copy` tags a pasted link so it is
 * distinguishable from an organic visit; `other` is the device's own
 * share sheet, which does not say where the link went.
 */
export type SharePlatform =
    | 'x'
    | 'facebook'
    | 'linkedin'
    | 'threads'
    | 'bluesky'
    | 'reddit'
    | 'whatsapp'
    | 'telegram'
    | 'pinterest'
    | 'tumblr'
    | 'nextdoor'
    | 'email'
    | 'sms'
    | 'copy'
    | 'instagram'
    | 'other';

/** Platforms whose composer is an app on the device, opened in place rather than in a new tab. */
const DEVICE_HANDLED_PLATFORMS: ReadonlySet<SharePlatform> = new Set<SharePlatform>(['email', 'sms']);

export type ShareTargetKind = 'listing' | 'open_house' | 'post';

export interface ShareListingFacts {
    listingId: string;
    /** Street line, e.g. "7 Hidden Springs Lane". */
    address?: string;
    /** "City, ST ZIP" when we have it split out. */
    locality?: string;
    /** Display strings straight off the listing, e.g. "$749,000", "4". */
    price?: string;
    beds?: string;
    baths?: string;
    sqft?: string;
}

/** The parts of an open house a share needs — both the public and the admin rows carry them. */
export interface OpenHouseShareFacts {
    id: number;
    title: string | null;
    full_address: string | null;
    property_short: string;
    /** Server-rendered "Sat, Sep 12 · 1:00–4:00 PM ET". */
    schedule_label: string;
}

export interface PostShareFacts {
    slug: string;
    title: string | null;
    subtitle?: string | null;
}

/** What the share sheet is about. Built with `listingShareTarget` & co. */
export interface ShareTarget {
    kind: ShareTargetKind;
    /** The listing id, the open house's numeric id, or the article slug. */
    id: string | number;
    /** Headline: the email subject and the Reddit title. */
    title: string;
    /** The sentence every composer prefills. */
    caption: string;
    /** Instagram post-kit facts — listings only, since Instagram takes no link. */
    listing?: { facts: ShareListingFacts; photoIndex: number };
}

const COPY_FALLBACK_MESSAGE = 'Could not copy. Long-press the link instead.';
const DEFAULT_LISTING_CAPTION = 'Take a look at this listing';
const DEFAULT_POST_TITLE = 'An article from Real Estate with Shayaan';

// Mirrors `_stat_line` / `share_caption` in
// backend/app/services/listing_share.py — the same sentence has to read
// the same whether a person shares it or a crawler renders it.
const FIRST_NUMBER = /\d[\d,]*(?:\.\d+)?/;

/**
 * The first number in a display string ("$749,000" → 749000, "2.5 Baths"
 * → 2.5, "3.0" → 3), or null when there is none. Decimals are kept, so
 * a half bath never reads as "25 ba".
 */
export function parseCount(raw?: string | null): number | null {
    const match = FIRST_NUMBER.exec(raw ?? '');
    if (!match) return null;
    const value = Number(match[0].replace(/,/g, ''));
    return Number.isFinite(value) ? value : null;
}

/** "$749,000 · 4 bd · 3 ba · 2,410 sqft", skipping whatever we don't know. */
export function listingStatLine(facts: ShareListingFacts): string {
    const parts: string[] = [];
    const price = parseCount(facts.price);
    if (price) parts.push(`$${price.toLocaleString('en-US')}`);
    const beds = parseCount(facts.beds);
    if (beds) parts.push(`${beds} bd`);
    const baths = parseCount(facts.baths);
    if (baths) parts.push(`${baths} ba`);
    const sqft = parseCount(facts.sqft);
    if (sqft) parts.push(`${sqft.toLocaleString('en-US')} sqft`);
    return parts.join(' · ');
}

/** The one line every platform prefills for a listing. */
export function listingCaption(facts: ShareListingFacts): string {
    const stats = listingStatLine(facts);
    const where = [facts.address, facts.locality].filter(Boolean).join(', ');
    if (stats && where) return `${stats} — ${where}`;
    return stats || where || DEFAULT_LISTING_CAPTION;
}

/** "Open house Sat, Sep 12 · 1:00–4:00 PM ET — 12 Oak St, Princeton, NJ". */
export function openHouseCaption(openHouse: OpenHouseShareFacts): string {
    const where = openHouse.full_address || openHouse.title || openHouse.property_short;
    return `Open house ${openHouse.schedule_label} — ${where}`;
}

/** "Title — subtitle", or just the title. */
export function postCaption(post: PostShareFacts): string {
    const title = post.title || DEFAULT_POST_TITLE;
    return post.subtitle ? `${title} — ${post.subtitle}` : title;
}

export function listingShareTarget(facts: ShareListingFacts, photoIndex = 0): ShareTarget {
    return {
        kind: 'listing',
        id: facts.listingId,
        title: facts.address || DEFAULT_LISTING_CAPTION,
        caption: listingCaption(facts),
        listing: { facts, photoIndex },
    };
}

export function openHouseShareTarget(openHouse: OpenHouseShareFacts): ShareTarget {
    return {
        kind: 'open_house',
        id: openHouse.id,
        title: `Open house: ${openHouse.title || openHouse.full_address || openHouse.property_short}`,
        caption: openHouseCaption(openHouse),
    };
}

export function postShareTarget(post: PostShareFacts): ShareTarget {
    return {
        kind: 'post',
        id: post.slug,
        title: post.title || DEFAULT_POST_TITLE,
        caption: postCaption(post),
    };
}

/**
 * The caption to paste into Instagram.
 *
 * Longer than the others because Instagram has no link to carry the
 * context — the post is the photo and this text, and the URL is only
 * useful in a bio or a story sticker.
 */
export function instagramCaption(facts: ShareListingFacts, url: string): string {
    const lines = [listingCaption(facts), '', 'Full details and photos:', url, ''];
    lines.push('#realestate #newjersey #homesforsale #justlisted #realtor');
    return lines.join('\n');
}

/**
 * The frontend origin. `API_ROOT_URL` points at the backend
 * (127.0.0.1:8000 in dev) and would produce a dead link. `APP_BASE_URL`
 * is the one export in config/api.ts that isn't stripped of a trailing
 * slash, hence the replace.
 */
function siteOrigin(): string {
    return APP_BASE_URL.replace(/\/+$/, '');
}

/** "realestatewithshayaan.com": who a share came from, where a composer asks (Nextdoor's `source`). */
function siteHost(): string {
    try {
        return new URL(siteOrigin()).host;
    } catch {
        return siteOrigin();
    }
}

/** The public path a share target lands on. */
export function shareTargetPath(target: Pick<ShareTarget, 'kind' | 'id'>): string {
    switch (target.kind) {
        case 'listing':
            return buildPath.propertyDetail(target.id);
        case 'open_house':
            return PATHS.OPEN_HOUSE;
        case 'post':
            return buildPath.postDetail(String(target.id));
    }
}

/**
 * The target's canonical URL, tagged with where it was shared.
 *
 * This is the fallback: the share sheet prefers a tracked short link
 * from `POST /social/share-links`, which redirects here with the same
 * `?src=` tag plus a `ref` code.
 */
export function shareTargetUrl(target: Pick<ShareTarget, 'kind' | 'id'>, platform?: SharePlatform): string {
    const url = `${siteOrigin()}${shareTargetPath(target)}`;
    return platform ? `${url}?src=${platform}` : url;
}

/** The listing's public URL, tagged with where it was shared. */
export function listingShareUrl(listingId: string, platform?: SharePlatform): string {
    return shareTargetUrl({ kind: 'listing', id: listingId }, platform);
}

/** Where the Instagram post kit gets the photo — the backend proxies it
 * because listing photos are cross-origin and can't be saved directly. */
export function listingPhotoDownloadUrl(listingId: string, index = 0): string {
    return `${API_BASE_URL}/property-scraping/property-photo/${encodeURIComponent(listingId)}?index=${index}&download=true`;
}

/**
 * The composer URL for a platform, or "" when it has none (Instagram,
 * copy, the device share sheet).
 *
 * Verified current: `twitter.com/intent/tweet` (the `x.com/intent/post`
 * alias behaves identically), `facebook.com/sharer/sharer.php`,
 * `linkedin.com/sharing/share-offsite`, `threads.net/intent/post`,
 * `bsky.app/intent/compose`, `reddit.com/submit`, `wa.me/?text=`,
 * `t.me/share/url`, `pinterest.com/pin/create/button` (Pinterest offers
 * the page's og:image to pin), `tumblr.com/widgets/share/tool`, and
 * Nextdoor's `nextdoor.com/sharekit/` (which needs a `source` and builds
 * its preview from the last URL in `body`). `sms:?&body=` is the one
 * form both iOS and Android accept.
 */
export function shareIntentUrl(platform: SharePlatform, url: string, text: string, title: string = text): string {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);
    const textWithUrl = encodeURIComponent(`${text} ${url}`);
    switch (platform) {
        case 'x':
            return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        case 'facebook':
            // Facebook ignores title/description/image params; the card
            // comes from the og: tags at `u`. `quote` still prefills the
            // person's own words above it.
            return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        case 'linkedin':
            return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        case 'threads':
            return `https://www.threads.net/intent/post?text=${textWithUrl}`;
        case 'bluesky':
            return `https://bsky.app/intent/compose?text=${textWithUrl}`;
        case 'reddit':
            return `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodeURIComponent(title)}`;
        case 'whatsapp':
            return `https://wa.me/?text=${textWithUrl}`;
        case 'telegram':
            return `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
        case 'pinterest':
            return `https://www.pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}`;
        case 'tumblr':
            return `https://www.tumblr.com/widgets/share/tool?posttype=link&canonicalUrl=${encodedUrl}`
                + `&title=${encodeURIComponent(title)}&caption=${encodedText}`;
        case 'nextdoor':
            return `https://nextdoor.com/sharekit/?source=${encodeURIComponent(siteHost())}&body=${textWithUrl}`;
        case 'email':
            return `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;
        case 'sms':
            return `sms:?&body=${textWithUrl}`;
        default:
            return '';
    }
}

/** True for platforms whose composer is an app on the device (mail, messages) rather than a web page. */
export function isDeviceHandled(platform: SharePlatform): boolean {
    return DEVICE_HANDLED_PLATFORMS.has(platform);
}

/**
 * Open a composer. Web composers get their own tab without a handle on
 * our window; `mailto:` and `sms:` navigate in place, which hands the
 * link to the device's app and leaves this page where it is instead of
 * leaving a blank tab behind.
 *
 * Returns false when the browser refused to open the tab. Popup blockers
 * (Safari's above all) refuse a `window.open` that runs after an awaited
 * network call — the share sheet mints the tracked link first — so the
 * caller must not count a share, or close the sheet, on a refusal. The
 * opener is severed by hand rather than with the `noopener` feature,
 * because that feature makes `window.open` return null even when the
 * tab opened, which would hide the refusal.
 */
export function openShareWindow(url: string, platform: SharePlatform = 'other'): boolean {
    if (isDeviceHandled(platform)) {
        window.open(url, '_self');
        return true;
    }
    const composer = window.open(url, '_blank');
    if (!composer) return false;
    composer.opener = null;
    return true;
}

/** The device has a share sheet of its own (`navigator.share`). */
export function supportsNativeShare(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/** A phone or tablet, where the device's share sheet is what people reach for first. */
export function isTouchDevice(): boolean {
    if (typeof window === 'undefined') return false;
    if (window.matchMedia?.('(pointer: coarse)').matches) return true;
    return (navigator.maxTouchPoints ?? 0) > 0;
}

/** Offer the device's own share sheet first: a touch device that has one. */
export function prefersNativeShare(): boolean {
    return supportsNativeShare() && isTouchDevice();
}

/**
 * Hand the link to the device's share sheet. Resolves false when the
 * person dismissed it or the device refused, so the caller can fall
 * back to the platform buttons instead of reporting a share that never
 * happened.
 */
export async function nativeShare(data: { title: string; text: string; url: string }): Promise<boolean> {
    if (!supportsNativeShare()) return false;
    try {
        await navigator.share(data);
        return true;
    } catch {
        return false;
    }
}

/**
 * Copy text and confirm honestly.
 *
 * Moved here from `components/admin/openhouse/QrShareCard.tsx`, which
 * re-exports it: it was the only copy helper in the app that reported a
 * rejected write instead of toasting success regardless, and a public
 * page shouldn't be importing from an admin component to get it.
 */
export function copyLinkToClipboard(text: string, successMessage = 'Link copied'): void {
    if (!navigator.clipboard?.writeText) {
        notify.warning(COPY_FALLBACK_MESSAGE);
        return;
    }
    navigator.clipboard.writeText(text).then(
        () => notify.success(successMessage),
        () => notify.warning(COPY_FALLBACK_MESSAGE),
    );
}

/** Copy, and tell the caller whether it actually worked. */
export async function copyText(text: string): Promise<boolean> {
    if (!navigator.clipboard?.writeText) return false;
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

/**
 * Save a file the browser fetched itself.
 *
 * Same shape as the download in `pages/admin/ArchivePage.tsx`, including
 * the delayed revoke — Safari needs the object URL to still be alive
 * when its save dialog opens.
 */
export async function downloadFile(url: string, suggestedName: string): Promise<boolean> {
    try {
        const response = await fetch(url);
        if (!response.ok) return false;
        const objectUrl = URL.createObjectURL(await response.blob());
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = suggestedName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        return true;
    } catch {
        return false;
    }
}
